import { useId } from 'react';
import { ArrowRenderProps } from 'react-joyride';

function getDimensions(placement: string, base: number, size: number) {
    const [side] = placement.split('-');
    switch (side) {
        case 'top':
        case 'bottom':
            return { width: base, height: size };
        case 'left':
        case 'right':
            return { width: size, height: base };
        default:
            return null;
    }
}

function getPoints(placement: string, base: number, size: number) {
    const [side] = placement.split('-');
    switch (side) {
        case 'top':
            return `0,0 ${base / 2},${size} ${base},0`;
        case 'bottom':
            return `${base},${size} ${base / 2},0 0,${size}`;
        case 'left':
            return `0,0 ${size},${base / 2} 0,${base}`;
        case 'right':
            return `${size},${base} ${size},0 0,${base / 2}`;
        default:
            return null;
    }
}

// Returns a clip rect that excludes the base edge of the arrow (the edge flush with the card),
// so the stroke only renders on the outer visible edges.
function getClipRect(placement: string, base: number, size: number) {
    const [side] = placement.split('-');
    const PAD = 4;
    switch (side) {
        case 'top':    // base edge is at y=0
            return { x: -PAD, y: 0.5, width: base + PAD * 2, height: size + PAD };
        case 'bottom': // base edge is at y=size
            return { x: -PAD, y: -PAD, width: base + PAD * 2, height: size - 0.5 };
        case 'left':   // base edge is at x=0
            return { x: 0.5, y: -PAD, width: size + PAD, height: base + PAD * 2 };
        case 'right':  // base edge is at x=size
            return { x: -PAD, y: -PAD, width: size - 0.5, height: base + PAD * 2 };
        default:
            return null;
    }
}

export default function JoyrideArrow({ base, placement, size }: ArrowRenderProps) {
    const id = useId();
    const clipId = `joyride-arrow-clip-${id}`;
    const dims = getDimensions(placement, base, size);
    const points = getPoints(placement, base, size);
    const clip = getClipRect(placement, base, size);

    if (!dims || !points || !clip) return null;

    return (
        <svg
            height={dims.height}
            width={dims.width}
            overflow="visible"
            xmlns="http://www.w3.org/2000/svg"
        >
            <defs>
                <clipPath id={clipId}>
                    <rect x={clip.x} y={clip.y} width={clip.width} height={clip.height} />
                </clipPath>
            </defs>
            <polygon
                style={{ fill: 'hsl(var(--card))' }}
                points={points}
            />
            <polygon
                style={{ fill: 'none', stroke: 'hsl(var(--border))', strokeWidth: 1 }}
                points={points}
                clipPath={`url(#${clipId})`}
            />
        </svg>
    );
}
